import { create } from 'zustand';

import type { NotificationView } from '@/lib/api/generated/model';

/** At most this many alerts on screen; the rest wait behind "+n more" (spec 136). */
export const MAX_VISIBLE_ALERTS = 3;

/** How many are kept at all, so a burst cannot grow the stack without bound. */
const MAX_KEPT_ALERTS = 20;

/** The tabs of one person keep each other's stacks in step over this channel. */
const CHANNEL_NAME = 'notification-alerts';

interface AlertStore {
  /** Oldest first; the stack shows the newest. */
  alerts: NotificationView[];
  push: (notification: NotificationView) => void;
  /** Close one alert, here and — unless it came from there — in the other tabs. */
  dismiss: (id: string, options?: { fromOtherTab?: boolean }) => void;
  clear: () => void;
}

let channel: BroadcastChannel | null | undefined;

/**
 * The channel to the person's other tabs, or `null` where there is none — a
 * browser without `BroadcastChannel` keeps each tab's stack to itself.
 */
function tabs(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof BroadcastChannel === 'undefined') {
    channel = null;
    return channel;
  }
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<{ dismiss?: string }>) => {
    const id = event.data.dismiss;
    if (typeof id === 'string') {
      useAlertStore.getState().dismiss(id, { fromOtherTab: true });
    }
  };
  return channel;
}

/**
 * The alerts on screen (spec 136). Fed by the stream, which already arrives
 * in every open tab; an alert opened or dismissed in one tab is closed in the
 * others, because read state is shared and an alert someone acted on is news
 * to nobody.
 */
export const useAlertStore = create<AlertStore>((set, get) => ({
  alerts: [],
  push: (notification) => {
    tabs();
    if (get().alerts.some((alert) => alert.id === notification.id)) return;
    set((state) => ({
      alerts: [...state.alerts, notification].slice(-MAX_KEPT_ALERTS),
    }));
  },
  dismiss: (id, options) => {
    if (!get().alerts.some((alert) => alert.id === id)) return;
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.id !== id),
    }));
    if (!options?.fromOtherTab) tabs()?.postMessage({ dismiss: id });
  },
  clear: () => {
    set({ alerts: [] });
  },
}));

/** Whether the inbox popover is open — so an alert can open it (spec 136). */
export const useInboxStore = create<{
  open: boolean;
  setOpen: (open: boolean) => void;
}>((set) => ({
  open: false,
  setOpen: (open) => {
    set({ open });
  },
}));
